/**
 * Record a completion — TRUS-486 §1.8.
 *
 * Group entry is the default, not an edge case: someone runs a session for twelve people
 * and records it once. The backend skips anyone already recorded for that training on
 * that date rather than double-counting, and this screen reports who was skipped instead
 * of silently succeeding.
 *
 * Records are never deleted here. A mistake is voided with a reason — these rows are
 * evidence under a retention obligation that began 1/1/2026.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Ban, ClipboardCheck, Paperclip, Plus, Upload } from 'lucide-react'
import { PageHeader, PageContainer, PageState, SectionHeader } from '@/components/weavein'
import StaffMultiSelect from '@/components/forms/StaffMultiSelect'
import { useOrgStaffOptions } from '@/components/forms/staffOptions'
import { Button, ErrorNote, FILTER_INPUT, Field, FilterBar, INPUT, Modal, Pill, SortTh, TableShell, Td, Th, useSort } from './ui'
import {
  Training,
  TrainingRecord,
  AttendanceImportResult,
  createRecords,
  formatDate,
  importAttendance,
  listRecords,
  listTrainings,
  uploadCertificate,
  voidRecord,
} from './api'
import { AttendanceImportDialog } from './AttendanceImportDialog'

const today = () => new Date().toISOString().slice(0, 10)

export default function RecordPage() {
  // TRUS-562: include org admins (CEO/Chief). Leaving them out showed their records as
  // "Staff #<id>" and kept them out of the Record completion picker entirely.
  const staffOptions = useOrgStaffOptions({ includeOrgAdmins: true })
  // List filters (TRUS-562: "they want to sort/filter in Training by staff").
  const [search, setSearch] = useState('')
  const [trainingFilter, setTrainingFilter] = useState('')
  const [reportedFilter, setReportedFilter] = useState('')
  const [certFilter, setCertFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [records, setRecords] = useState<TrainingRecord[]>([])
  const [trainings, setTrainings] = useState<Training[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [entryOpen, setEntryOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [trainingId, setTrainingId] = useState('')
  const [trainingDate, setTrainingDate] = useState(today())
  const [userIds, setUserIds] = useState<string[]>([])
  const [hours, setHours] = useState('')
  const [source, setSource] = useState('manual')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const [voiding, setVoiding] = useState<TrainingRecord | null>(null)
  const [voidReason, setVoidReason] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [r, t] = await Promise.all([listRecords(), listTrainings()])
      setRecords(r.records)
      setTrainings(t.trainings)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load completion records.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const selectedTraining = useMemo(
    () => trainings.find((t) => String(t.id) === trainingId) ?? null,
    [trainings, trainingId],
  )

  const resetEntry = () => {
    setTrainingId('')
    setTrainingDate(today())
    setUserIds([])
    setHours('')
    setSource('manual')
    setNotes('')
  }

  const submit = async () => {
    if (!trainingId || userIds.length === 0) return
    setSaving(true)
    setActionError(null)
    try {
      const result = await createRecords({
        training_id: Number(trainingId),
        training_date: trainingDate,
        user_ids: userIds.map((v) => Number(v)).filter((n) => Number.isFinite(n)),
        hours: hours === '' ? null : Number(hours),
        source,
        notes: notes.trim() || null,
      })
      const skipped = result.already_recorded_user_ids.length
      // TRUS-562: a one-time training is completed once; people who already have it are skipped.
      const alreadyDone = result.already_completed_user_ids?.length ?? 0
      setNotice(
        [
          `Recorded ${result.created} completion${result.created === 1 ? '' : 's'}.`,
          skipped > 0 ? `${skipped} already had this training on that date and were left alone.` : '',
          alreadyDone > 0
            ? `${alreadyDone} had already completed this one-time training and were left alone.`
            : '',
        ]
          .filter(Boolean)
          .join(' '),
      )
      setEntryOpen(false)
      resetEntry()
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not record the completion.')
    } finally {
      setSaving(false)
    }
  }

  const attach = async (record: TrainingRecord, file: File) => {
    setActionError(null)
    try {
      await uploadCertificate(record.id, file)
      setNotice(`Certificate attached to ${record.training_title}.`)
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not attach the certificate.')
    }
  }

  const doVoid = async () => {
    if (!voiding || voidReason.trim().length < 3) return
    setActionError(null)
    try {
      await voidRecord(voiding.id, voidReason.trim())
      setVoiding(null)
      setVoidReason('')
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not void the record.')
    }
  }

  const staffName = (userId: number) =>
    staffOptions.find((o) => o.value === String(userId))?.label ?? `Staff #${userId}`

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return records.filter((r) => {
      if (needle && !`${staffName(r.user_id)} ${r.training_title}`.toLowerCase().includes(needle)) return false
      if (trainingFilter && String(r.training_id) !== trainingFilter) return false
      if (reportedFilter && r.reported_by !== reportedFilter) return false
      if (certFilter === 'missing' && !r.certificate_missing) return false
      if (certFilter === 'attached' && !r.certificate) return false
      if (dateFrom && r.training_date < dateFrom) return false
      if (dateTo && r.training_date > dateTo) return false
      return true
    })
    // staffName reads staffOptions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, staffOptions, search, trainingFilter, reportedFilter, certFilter, dateFrom, dateTo])

  const { sorted, sort, toggle } = useSort(
    filtered,
    {
      staff: (r) => staffName(r.user_id),
      training: (r) => r.training_title,
      date: (r) => r.training_date,
      hours: (r) => r.hours,
      reported: (r) => r.reported_by,
    },
    { key: 'date', dir: 'desc' },
  )
  const filtersOn = Boolean(search || trainingFilter || reportedFilter || certFilter || dateFrom || dateTo)
  const clearFilters = () => {
    setSearch('')
    setTrainingFilter('')
    setReportedFilter('')
    setCertFilter('')
    setDateFrom('')
    setDateTo('')
  }
  // Trainings that have records, for the filter.
  const recordTrainings = useMemo(() => {
    const seen = new Map<number, string>()
    records.forEach((r) => seen.set(r.training_id, r.training_title))
    return Array.from(seen, ([id, title]) => ({ id, title })).sort((a, b) => a.title.localeCompare(b.title))
  }, [records])

  return (
    <>
      <PageHeader
        eyebrow="Training · Records"
        title="Completion records"
        description="Record who completed which training, individually or as a group."
        actions={
          <>
            <Button onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" /> Import attendance
            </Button>
            <Button variant="primary" onClick={() => setEntryOpen(true)}>
              <Plus className="h-4 w-4" /> Record completion
            </Button>
          </>
        }
      />
      <PageContainer>
        <div className="space-y-4">
          {actionError && <ErrorNote message={actionError} onDismiss={() => setActionError(null)} />}
          {notice && (
            <div className="rounded-[10px] border border-success bg-success-soft px-4 py-3 text-[13px] text-success-ink">
              {notice}
            </div>
          )}

          {loading ? (
            <PageState variant="loading" title="Loading records" />
          ) : error ? (
            <PageState
              variant="error"
              title="Could not load records"
              message={error}
              action={<Button onClick={() => void load()}>Retry</Button>}
            />
          ) : records.length === 0 ? (
            <PageState
              variant="empty"
              icon={<ClipboardCheck className="h-5 w-5 text-ink-400" />}
              title="No completions recorded yet"
              message={
                trainings.length === 0
                  ? 'Add a training to the catalogue first, then record who has completed it.'
                  : 'Record a session that has already happened — in a room, on a call, or with an external provider.'
              }
              action={
                trainings.length > 0 ? (
                  <Button variant="primary" onClick={() => setEntryOpen(true)}>
                    <Plus className="h-4 w-4" /> Record completion
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <FilterBar>
                <input
                  type="search"
                  className={`${FILTER_INPUT} min-w-[220px] flex-1`}
                  placeholder="Search staff or training"
                  aria-label="Search staff or training"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select className={FILTER_INPUT} aria-label="Filter by training" value={trainingFilter} onChange={(e) => setTrainingFilter(e.target.value)}>
                  <option value="">All trainings</option>
                  {recordTrainings.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.title}
                    </option>
                  ))}
                </select>
                <select className={FILTER_INPUT} aria-label="Filter by who reported" value={reportedFilter} onChange={(e) => setReportedFilter(e.target.value)}>
                  <option value="">Reported by anyone</option>
                  <option value="self">Self-reported</option>
                  <option value="admin">Admin</option>
                </select>
                <select className={FILTER_INPUT} aria-label="Filter by certificate" value={certFilter} onChange={(e) => setCertFilter(e.target.value)}>
                  <option value="">Any certificate</option>
                  <option value="missing">Certificate missing</option>
                  <option value="attached">Certificate attached</option>
                </select>
                <label className="flex items-center gap-1 text-[12px] text-ink-500">
                  From
                  <input type="date" className={FILTER_INPUT} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </label>
                <label className="flex items-center gap-1 text-[12px] text-ink-500">
                  To
                  <input type="date" className={FILTER_INPUT} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </label>
                {filtersOn && (
                  <Button variant="ghost" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
              </FilterBar>
              <SectionHeader
                title={
                  filtersOn
                    ? `${sorted.length} of ${records.length} records`
                    : `${records.length} record${records.length === 1 ? '' : 's'}`
                }
              />
              <TableShell>
                <thead>
                  <tr>
                    <SortTh label="Staff" sortKey="staff" sort={sort} onSort={toggle} />
                    <SortTh label="Training" sortKey="training" sort={sort} onSort={toggle} />
                    <SortTh label="Date" sortKey="date" sort={sort} onSort={toggle} />
                    <SortTh label="Hours" sortKey="hours" sort={sort} onSort={toggle} />
                    <SortTh label="Reported by" sortKey="reported" sort={sort} onSort={toggle} />
                    <Th>Certificate</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 && (
                    <tr>
                      <Td colSpan={7} className="text-ink-500">
                        No records match these filters.
                      </Td>
                    </tr>
                  )}
                  {sorted.map((r) => (
                    <tr key={r.id} className="hover:bg-paper-2">
                      <Td className="font-medium">{staffName(r.user_id)}</Td>
                      <Td>{r.training_title}</Td>
                      <Td className="whitespace-nowrap">{formatDate(r.training_date)}</Td>
                      <Td className="tabular-nums">{r.hours === null ? '—' : r.hours}</Td>
                      <Td>
                        <Pill tone={r.reported_by === 'self' ? 'neutral' : 'accent'}>
                          {r.reported_by === 'self' ? 'Self-reported' : 'Admin'}
                        </Pill>
                      </Td>
                      <Td>
                        {r.certificate ? (
                          <span className="inline-flex items-center gap-1 text-ink-500">
                            <Paperclip className="h-3.5 w-3.5" />
                            {r.certificate.filename}
                          </span>
                        ) : (
                          <label className="cursor-pointer text-[13px] text-v2accent hover:underline">
                            {/* TRUS-562: the training says a certificate is
                                required and this completion has none yet. The
                                completion still counts — what is outstanding is
                                the evidence, and saying so is the whole point of
                                the flag. */}
                            {r.certificate_missing && (
                              <span className="mr-2 inline-flex items-center gap-1 text-attention-ink">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Required
                              </span>
                            )}
                            Attach
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx"
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) void attach(r, file)
                                e.target.value = ''
                              }}
                            />
                          </label>
                        )}
                      </Td>
                      <Td>
                        <div className="flex justify-end">
                          <Button variant="ghost" onClick={() => setVoiding(r)} title="Void this record">
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </TableShell>
            </>
          )}
        </div>
      </PageContainer>

      <Modal
        open={entryOpen}
        title="Record a completion"
        description="Pick everyone who attended — one entry covers the whole group."
        onClose={() => setEntryOpen(false)}
        footer={
          <>
            <Button onClick={() => setEntryOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={!trainingId || userIds.length === 0 || saving}
            >
              {saving ? 'Recording…' : `Record ${userIds.length || ''}`.trim()}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Training" required>
            <select className={INPUT} value={trainingId} onChange={(e) => setTrainingId(e.target.value)}>
              <option value="">Choose a training</option>
              {trainings.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date of training" required hint="Cannot be in the future.">
              <input
                className={INPUT}
                type="date"
                max={today()}
                value={trainingDate}
                onChange={(e) => setTrainingDate(e.target.value)}
              />
            </Field>
            <Field
              label="Hours"
              hint={
                selectedTraining?.hours
                  ? `Defaults to ${selectedTraining.hours} from the training if left blank.`
                  : 'Optional.'
              }
            >
              <input
                className={INPUT}
                type="number"
                min="0"
                step="0.25"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
              />
            </Field>
          </div>

          <div>
            <div className="mb-1 text-[13px] font-medium text-ink">
              Who attended<span className="ml-1 text-danger">*</span>
            </div>
            <StaffMultiSelect
              options={staffOptions}
              value={userIds}
              onChange={setUserIds}
              placeholder="Search staff"
              emptyHint="No staff accounts in this organization yet."
            />
            <div className="mt-1 text-[12px] text-ink-400">{userIds.length} selected</div>
          </div>

          <Field label="Where did it come from?" hint="Use “External provider” for CalChamber and similar.">
            <select className={INPUT} value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="manual">Recorded by hand</option>
              <option value="external">External provider</option>
              <option value="calendar">A scheduled session</option>
              <option value="import">A file import</option>
            </select>
          </Field>

          <Field label="Notes">
            <textarea className={INPUT} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
      </Modal>

      <Modal
        open={voiding !== null}
        title="Void this record"
        description="Records are never deleted — voiding keeps the history and asks why."
        onClose={() => {
          setVoiding(null)
          setVoidReason('')
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setVoiding(null)
                setVoidReason('')
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void doVoid()} disabled={voidReason.trim().length < 3}>
              Void record
            </Button>
          </>
        }
      >
        {voiding && (
          <div className="space-y-4">
            <div className="text-[13px] text-ink-500">
              {staffName(voiding.user_id)} — {voiding.training_title}, {formatDate(voiding.training_date)}
            </div>
            <Field label="Reason" required hint="Stored on the record's audit trail.">
              <input
                className={INPUT}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="e.g. recorded against the wrong person"
              />
            </Field>
          </div>
        )}
      </Modal>

      <AttendanceImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => void load()}
      />
    </>
  )
}
